import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:9000/api";

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Get all products
export const getProducts = async () => {
  try {
    const response = await api.get("/products");
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error("Network error");
  }
};

// Get product by ID
export const getProductById = async (id) => {
  try {
    const response = await api.get(`/products/${id}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error("Network error");
  }
};

// Create new product
export const createProduct = async (productData) => {
  try {
    const response = await api.post("/products", productData);
    return response.data.product;
  } catch (error) {
    throw error.response ? error.response.data : new Error("Network error");
  }
};

// Update product
export const updateProduct = async (id, productData) => {
  try {
    const response = await api.put(`/products/${id}`, productData);
    return response.data.product;
  } catch (error) {
    throw error.response ? error.response.data : new Error("Network error");
  }
};

// Delete product
export const deleteProduct = async (id) => {
  try {
    const response = await api.delete(`/products/${id}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error("Network error");
  }
};

// Get product versions
export const getProductVersions = async (productId) => {
  try {
    const response = await api.get(`/products/${productId}/versions`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error("Network error");
  }
};

// Get specific product version
export const getProductVersionById = async (versionId) => {
  try {
    // This assumes the backend has an endpoint for a specific version
    // If not available, we can fetch the product and filter the versions
    const response = await api.get(`/products/versions/${versionId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching product version by ID:", error);

    // If backend endpoint fails, try alternative approach
    if (error.response && error.response.status === 404) {
      try {
        // Fetch all products and find the one with this version
        const allProducts = await getProducts();
        for (const product of allProducts) {
          if (product.ProductVersions) {
            const foundVersion = product.ProductVersions.find(
              (v) => v.id.toString() === versionId.toString()
            );
            if (foundVersion) {
              // Add the product to the version for consistency
              foundVersion.product = product;
              return foundVersion;
            }
          }
        }
      } catch (fallbackError) {
        console.error("Fallback approach also failed:", fallbackError);
      }
    }

    throw error.response ? error.response.data : new Error("Network error");
  }
};

// Create new product version
export const createProductVersion = async (productId, versionData) => {
  try {
    const response = await api.post(
      `/products/${productId}/versions`,
      versionData
    );
    return response.data.version;
  } catch (error) {
    throw error.response ? error.response.data : new Error("Network error");
  }
};

// Update product version
export const updateProductVersion = async (
  productId,
  versionId,
  versionData
) => {
  const maxRetries = 3;
  let retryCount = 0;

  while (retryCount < maxRetries) {
    try {
      const response = await api.put(
        `/products/${productId}/versions/${versionId}`,
        versionData
      );
      return response.data.version;
    } catch (error) {
      if (error.response?.status === 429 && retryCount < maxRetries - 1) {
        // Wait for 1 second before retrying
        await new Promise((resolve) => setTimeout(resolve, 1000));
        retryCount++;
        continue;
      }
      throw error.response ? error.response.data : new Error("Network error");
    }
  }
};

// Set version as latest
export const setVersionAsLatest = async (productId, versionId) => {
  try {
    const response = await api.patch(
      `/products/${productId}/versions/${versionId}/set-latest`
    );
    return response.data.version;
  } catch (error) {
    throw error.response ? error.response.data : new Error("Network error");
  }
};

// Delete product version
export const deleteProductVersion = async (productId, versionId) => {
  try {
    const response = await api.delete(
      `/products/${productId}/versions/${versionId}`
    );
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error("Network error");
  }
};

// Start download
export const startDownload = async (versionId) => {
  try {
    const response = await api.post("/downloads/start", { versionId });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : new Error("Network error");
  }
};
